import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../../lib/api";
import { AlertTriangle, Search, CloudRain, Sun, Droplets, ChevronRight, Sunrise, Sprout } from "lucide-react";

const pick = (lang, hi, en) => (lang === "हिंदी" ? hi : en);
const ICONS = { droplets: Droplets, search: Search, "cloud-rain": CloudRain, sun: Sun, "alert-triangle": AlertTriangle, sprout: Sprout };
const TONE = {
  act: { bar: "#EF4444", chip: "bg-red-50 text-red-600", label: { hi: "पहला काम", en: "Do first" } },
  check: { bar: "#F59E0B", chip: "bg-amber-50 text-amber-600", label: { hi: "जाँच", en: "Check" } },
  know: { bar: "#3B82F6", chip: "bg-blue-50 text-blue-600", label: { hi: "मौसम", en: "Weather" } },
};

export function MorningBrief({ language, item }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const hour = new Date().getHours();
  const isMorning = hour >= 4 && hour < 15;

  useEffect(() => {
    if (!isMorning) return;
    api.get("/data/fields").then((r) => {
      const c = (r.data || [])[0]?.center || { lat: 24.4567, lng: 74.8736 };
      api.get(`/checkin/today-tasks?lat=${c.lat}&lng=${c.lng}`).then((res) => setData(res.data)).catch(() => setData(false));
    }).catch(() => api.get("/checkin/today-tasks").then((res) => setData(res.data)).catch(() => setData(false)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isMorning || !data || !data.cards?.length) return null;

  const go = (c) => { if (c.target === "scout") navigate(`/crop-check${c.cycleId ? `?cycleId=${c.cycleId}` : ""}`); else if (c.target === "plan") navigate(`/plant?cropId=${c.cropId}&plannedId=${c.planId}`); else if (c.target === "pestmap") navigate("/pest-map"); else if (c.target === "checkin") navigate("/check-in"); };

  return (
    <motion.div variants={item} className="mb-5" data-testid="morning-brief">
      <div className="flex items-center gap-2 mb-3 px-1">
        <Sunrise className="h-5 w-5 text-[#F59E0B]" />
        <h2 className="font-display text-lg font-extrabold text-[#16351d]">{pick(language, "आज क्या करें", "What to do today")}</h2>
        <span className="text-xs text-[#8a998a] ml-auto">{new Date().toLocaleDateString(language === "हिंदी" ? "hi-IN" : "en-US", { weekday: "short", day: "numeric", month: "short" })}</span>
      </div>
      <div className="space-y-3">
        {data.cards.map((c, i) => {
          const t = TONE[c.type] || TONE.know;
          const Icon = ICONS[c.icon] || Sun;
          const clickable = !!c.target;
          return (
            <motion.button key={i} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 * i }}
              onClick={() => clickable && go(c)} disabled={!clickable} data-testid={`brief-${c.type}`}
              className={`w-full text-left rounded-2xl bg-white border border-[#eef2ec] overflow-hidden flex items-stretch shadow-[0_8px_30px_-18px_rgba(20,50,25,0.25)] ${clickable ? "active:scale-[.99] transition-transform" : "cursor-default"}`}>
              <span className="w-1.5 shrink-0" style={{ background: t.bar }} />
              <div className="flex items-start gap-3 p-4 flex-1 min-w-0">
                <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${t.bar}18` }}><Icon className="h-5 w-5" style={{ color: t.bar }} /></span>
                <div className="min-w-0 flex-1">
                  <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${t.chip}`}>{pick(language, t.label.hi, t.label.en)}</span>
                  <p className="font-bold text-[15px] text-[#16351d] leading-snug mt-1.5">{c.title}</p>
                  <p className="text-[13px] text-[#6c7a67] mt-0.5 leading-relaxed">{c.body}</p>
                </div>
                {clickable && <div className="self-center flex items-center gap-1 text-[#15803D] font-semibold text-sm shrink-0"><span className="hidden sm:inline">{c.cta}</span><ChevronRight className="h-5 w-5" /></div>}
              </div>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}
